import React from 'react';
import { ColorValue, ProgressBarRadius, ProgressBarSize, useComponentDefaultProps } from '../../theming';
import { Styler, StylerProps } from '../Styler';
import { TypographyBody } from '../Typography';
import { PROGRESS_BAR_STATIC_STYLES, useProgressBarCssVariables } from './ProgressBar.styles';

export interface ProgressBarProps extends StylerProps {
    /**
     * The label which will be displayed onm the progress bar.
     */
    children?: React.ReactNode;

    /**
     * The color of the completed portion of the progress bar.
     * @default primary.base
     */
    color?: ColorValue;

    /**
     * The completed progress value.
     */
    value: number;

    /**
     * The border radius applied to the progress bar track.
     * @default 'rounded'
     */
    radius?: ProgressBarRadius | string;

    /**
     * The height of the progress bar track.
     * @default 'm'
     */
    size?: ProgressBarSize | string;
}

const defaultProps: Partial<ProgressBarProps> = {
    color: 'primary.base',
    radius: 'rounded',
    size: 'm',
};

export function ProgressBar(props: Readonly<ProgressBarProps>): React.JSX.Element {
    const {
        children,
        color = 'primary.base',
        value,
        size = 'm',
        radius = 'rounded',
        ref,
        ...other
    } = useComponentDefaultProps('ProgressBar', defaultProps, props);

    const cssVariables = useProgressBarCssVariables(value, color, size, radius);

    return (
        <Styler
            ref={ref}
            style={cssVariables}
            baseStyles={[PROGRESS_BAR_STATIC_STYLES]}
            data-progress-bar={true}
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={value}
            aria-valuetext={`${value}%`}
            {...other}
        >
            <div data-progress-bar-inner={true}>
                {children && (
                    <TypographyBody size="xs" weight="semiBold" data-progress-bar-label={true}>
                        {children}
                    </TypographyBody>
                )}
            </div>
        </Styler>
    );
}
